import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactTooltip from 'react-tooltip';

import { IContact } from '@models/Contact.model';
import { useContext, useState } from 'react';
import { AssetContext } from '@pages/Asset/Asset.query';
import { ContactsItemMenu } from '@components/AssetPageMenus/ContactsItemMenu/ContactsItemMenu';

import { maskString } from '@utils';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { useSelector } from 'react-redux';
import {
  Container,
  Title,
  Head,
  Row,
  LighterText,
  Divider,
  IconWrapper,
  Item,
  LineDivider,
  ItemData,
  IconButton,
  Items,
  ItemText,
  TitleSection,
  Wrapper,
} from './ContactInfo.styles';

interface IContactInfo {
  contact: IContact;
}

const ContactInfo = ({ contact }: IContactInfo) => {
  const { selectedContactId } = useContext(AssetContext);

  const [showClipboard, setShowClipboard] = useState<string | null>(null);
  const { isDesktop } = useSelector(checkDevice());

  return (
    <Container>
      <Head>
        <Row className="header">
          <IconButton
            className="backIcon"
            onClick={() => selectedContactId.set(null)}
          >
            <BlueIcon icon="arrow-left" color="#8F95B2" />
          </IconButton>
          <Wrapper>
            <Title>{contact.name}</Title>
          </Wrapper>
          {isDesktop && <ContactsItemMenu contact={contact} />}
        </Row>
        <LighterText className="header">{contact.title}</LighterText>
      </Head>

      <Items>
        {contact.phones.length > 0 && (
          <Row className="fieldLabel">
            <IconWrapper>
              <BlueIcon icon="phone" color="#1371FE" />
            </IconWrapper>
            <TitleSection>Phone Numbers</TitleSection>
          </Row>
        )}

        {contact.phones.map(phone => (
          <div key={phone.id}>
            <Item
              onMouseOver={() => setShowClipboard(phone.id)}
              onMouseLeave={() => setShowClipboard(null)}
            >
              <ItemData>
                <ItemText>
                  {phone.phone.length === 10
                    ? maskString(phone.phone, '(###) ###-####')
                    : phone.phone}
                </ItemText>
                <LighterText>{phone.label}</LighterText>
              </ItemData>

              {showClipboard === phone.id && (
                <>
                  <IconButton
                    data-tip="Copied!"
                    data-for={`tooltipPhone-${phone.id}`}
                    data-event="click"
                  >
                    <BlueIcon icon="duplicate" color="#8F95B2" />
                  </IconButton>
                  <ReactTooltip
                    id={`tooltipPhone-${phone.id}`}
                    className="tooltip"
                    effect="solid"
                    globalEventOff="click"
                    afterShow={() => {
                      if (phone.phone) {
                        setTimeout(ReactTooltip.hide, 600);
                        navigator.clipboard.writeText(phone.phone);
                      }
                    }}
                  />
                </>
              )}
            </Item>
            <LineDivider />
          </div>
        ))}

        {contact.emails.length > 0 && (
          <>
            <Divider height="16px" />
            <Row className="fieldLabel">
              <IconWrapper>
                <BlueIcon icon="envelope" color="#1371FE" />
              </IconWrapper>
              <TitleSection>Emails</TitleSection>
            </Row>
          </>
        )}

        {contact.emails.map(email => (
          <div key={email.id}>
            <Item
              onMouseOver={() => setShowClipboard(email.id)}
              onMouseLeave={() => setShowClipboard(null)}
            >
              <ItemData>
                <ItemText>{email.email}</ItemText>
                <LighterText>{email.label}</LighterText>
              </ItemData>

              {showClipboard === email.id && (
                <>
                  <IconButton
                    data-tip="Copied!"
                    data-for={`tooltipEmail-${email.id}`}
                    data-event="click"
                  >
                    <BlueIcon icon="duplicate" color="#8F95B2" />
                  </IconButton>
                  <ReactTooltip
                    id={`tooltipEmail-${email.id}`}
                    className="tooltip"
                    effect="solid"
                    globalEventOff="click"
                    afterShow={() => {
                      if (email.email) {
                        setTimeout(ReactTooltip.hide, 600);
                        navigator.clipboard.writeText(email.email);
                      }
                    }}
                  />
                </>
              )}
            </Item>
            <LineDivider />
          </div>
        ))}
      </Items>
    </Container>
  );
};

export { ContactInfo };
